// Default/fallback-served and QA members, and events outside the assignment
// window, never count toward a greeting readout.
const GREETING_METRIC_EXCLUSIONS = [
  'default-assignment',
  'fallback-assignment',
  'qa-member',
  'pre-assignment',
  'post-window',
] as const;

export const journeySonaGreetingExperimentDefinition = {
  experiment_key: 'experiment-journey-sona-greeting-v1',
  version: '1.0.0',
  status: 'running',
  owner: 'product-analytics',
  assignment_source: 'posthog',
  flag_key: 'flag-journey-sona-greeting-variant',
  hypothesis: {
    id: 'journey-sona-greeting-session-completion',
    owner: 'product-analytics',
    statement:
      'Member-centred framing in the greeting increases session completion compared with agent-centred framing.',
    expected_direction: 'increase',
    target_population:
      'Authenticated members assigned by sticky flag evaluation at journey-sona session mint.',
    decision_criteria:
      'Ship momentum when session completion rate rises and sessions started per member do not decrease; otherwise retain settled.',
  },
  primary_metric: {
    id: 'greeting.journey-sona.session-completion-rate',
    event: 'server_outcome',
    event_properties: [{ key: 'outcome_id', value: 'voice.session.completed' }],
    analysis_source: 'posthog_experiment',
    aggregation: 'conversion_rate',
    denominator: 'assigned-members',
    expected_direction: 'increase',
    exclusions: GREETING_METRIC_EXCLUSIONS,
    window_days: 14,
    minimum_sample_size: 200,
  },
  guardrail_metrics: [
    {
      id: 'greeting.journey-sona.sessions-started',
      event: 'server_outcome',
      event_properties: [{ key: 'outcome_id', value: 'voice.session.created' }],
      analysis_source: 'posthog_experiment',
      aggregation: 'mean',
      denominator: 'assigned-members',
      expected_direction: 'increase',
      exclusions: GREETING_METRIC_EXCLUSIONS,
      window_days: 14,
      minimum_sample_size: 200,
    },
  ],
  readout: {
    cadence: 'weekly',
    owner: 'product-analytics',
    primary_metric_id: 'greeting.journey-sona.session-completion-rate',
    guardrail_metric_ids: ['greeting.journey-sona.sessions-started'],
    query_template: 'query-greeting-experiment-result',
  },
  targeting: {
    enrollment_mode: 'automatic',
    rollout_percent: 100,
    salt: 'experiment-journey-sona-greeting-v1',
    platforms: ['api'],
  },
  variants: [
    {
      key: 'settled',
      label: 'Settled greeting framing',
      allocation_percent: 50,
      control: true,
      targets: [
        {
          scope: 'first_message',
          agent_definition_id: 'journey-sona',
          agent_definition_version: '1.2.4',
          first_message_pool: 'settled',
        },
      ],
    },
    {
      key: 'momentum',
      label: 'Momentum greeting framing',
      allocation_percent: 50,
      control: false,
      targets: [
        {
          scope: 'first_message',
          agent_definition_id: 'journey-sona',
          agent_definition_version: '1.2.4',
          first_message_pool: 'momentum',
        },
      ],
    },
  ],
} as const;

export const baseSonaGreetingExperimentDefinition = {
  experiment_key: 'experiment-base-sona-greeting-v1',
  version: '1.0.0',
  status: 'running',
  owner: 'product-analytics',
  assignment_source: 'posthog',
  flag_key: 'flag-base-sona-greeting-variant',
  hypothesis: {
    id: 'base-sona-greeting-session-completion',
    owner: 'product-analytics',
    statement:
      'Member-centred framing in the greeting increases session completion compared with agent-centred framing.',
    expected_direction: 'increase',
    target_population:
      'Authenticated members assigned by sticky flag evaluation at base-sona session mint.',
    decision_criteria:
      'Ship momentum when session completion rate rises and sessions started per member do not decrease; otherwise retain settled.',
  },
  primary_metric: {
    id: 'greeting.base-sona.session-completion-rate',
    event: 'server_outcome',
    event_properties: [{ key: 'outcome_id', value: 'voice.session.completed' }],
    analysis_source: 'posthog_experiment',
    aggregation: 'conversion_rate',
    denominator: 'assigned-members',
    expected_direction: 'increase',
    exclusions: GREETING_METRIC_EXCLUSIONS,
    window_days: 14,
    minimum_sample_size: 200,
  },
  guardrail_metrics: [
    {
      id: 'greeting.base-sona.sessions-started',
      event: 'server_outcome',
      event_properties: [{ key: 'outcome_id', value: 'voice.session.created' }],
      analysis_source: 'posthog_experiment',
      aggregation: 'mean',
      denominator: 'assigned-members',
      expected_direction: 'increase',
      exclusions: GREETING_METRIC_EXCLUSIONS,
      window_days: 14,
      minimum_sample_size: 200,
    },
  ],
  readout: {
    cadence: 'weekly',
    owner: 'product-analytics',
    primary_metric_id: 'greeting.base-sona.session-completion-rate',
    guardrail_metric_ids: ['greeting.base-sona.sessions-started'],
    query_template: 'query-greeting-experiment-result',
  },
  targeting: {
    enrollment_mode: 'automatic',
    rollout_percent: 100,
    salt: 'experiment-base-sona-greeting-v1',
    platforms: ['api'],
  },
  variants: [
    {
      key: 'settled',
      label: 'Settled greeting framing',
      allocation_percent: 50,
      control: true,
      targets: [
        {
          scope: 'first_message',
          agent_definition_id: 'base-sona',
          agent_definition_version: '1.3.5',
          first_message_pool: 'settled',
        },
      ],
    },
    {
      key: 'momentum',
      label: 'Momentum greeting framing',
      allocation_percent: 50,
      control: false,
      targets: [
        {
          scope: 'first_message',
          agent_definition_id: 'base-sona',
          agent_definition_version: '1.3.5',
          first_message_pool: 'momentum',
        },
      ],
    },
  ],
} as const;

const productExperimentDefinitions = [
  journeySonaGreetingExperimentDefinition,
  baseSonaGreetingExperimentDefinition,
] as const;

export function listProductExperimentDefinitions(): readonly unknown[] {
  return productExperimentDefinitions;
}
